import { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { ChevronDownIcon, ShieldCheckIcon } from "@heroicons/react/20/solid";
import useGeneralStore from "store/general";
import { useSuperAdminAuthStore } from "store/superAdminAuth";
import { useNavigate } from "react-router-dom";
import { superAdminUserNavigation } from "utils/navigation";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function SuperAdminTopNavbar({ setSidebarOpen }) {
  const { sidebarCollapsed, toggleSidebar } = useGeneralStore();
  const username = useSuperAdminAuthStore((state) => state.username);
  const clearSuperAdminAuthData = useSuperAdminAuthStore(
    (state) => state.clearSuperAdminAuthData
  );
  const navigate = useNavigate();

  const handleLogout = () => {
    clearSuperAdminAuthData();
    navigate("/super-admin-login");
  };

  return (
    <div className="sticky top-0 z-10 flex h-16 shrink-0 items-center gap-x-2 md:gap-x-3 lg:gap-x-4 border-b border-purple-200 bg-white px-2 md:px-3 lg:px-4 shadow-sm">
      <button
        type="button"
        className="-m-1.5 p-1.5 text-gray-700 lg:hidden"
        onClick={() => setSidebarOpen(true)}
      >
        <span className="sr-only">Open sidebar</span>
        <Bars3Icon className="h-4 w-4 md:h-5 md:w-5" aria-hidden="true" />
      </button>

      {/* Collapse button for desktop */}
      <button
        type="button"
        className="hidden lg:block -m-2.5 p-2.5 text-gray-700 hover:text-gray-900"
        onClick={toggleSidebar}
        title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <span className="sr-only">
          {sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        </span>
        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
      </button>

      {/* Separator */}
      <div
        className="h-4 md:h-5 lg:h-6 w-px bg-gray-900/10 lg:hidden"
        aria-hidden="true"
      />

      <div className="flex flex-1 gap-x-2 md:gap-x-3 lg:gap-x-4 self-stretch items-center">
        {/* Super Admin Portal Title */}
        <div className="flex items-center flex-1 min-w-0 gap-x-2">
          <ShieldCheckIcon className="h-5 w-5 md:h-6 md:w-6 text-purple-600 flex-shrink-0" />
          <h1 className="text-xs md:text-sm lg:text-lg font-semibold leading-tight text-gray-900 break-words line-clamp-2">
            Super Administrator Portal
          </h1>
        </div>

        <div className="flex items-center gap-x-2 md:gap-x-3 lg:gap-x-4 flex-shrink-0">
          {/* Super Admin Badge */}
          <span className="inline-flex items-center gap-x-1.5 rounded-full bg-purple-100 px-2 md:px-3 py-1 text-xs font-medium text-purple-700">
            <svg
              className="h-1.5 w-1.5 fill-purple-500"
              viewBox="0 0 6 6"
              aria-hidden="true"
            >
              <circle cx={3} cy={3} r={3} />
            </svg>
            Super Admin
          </span>

          {/* Separator */}
          <div
            className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10"
            aria-hidden="true"
          />

          {/* Profile dropdown */}
          <Menu as="div" className="relative">
            <Menu.Button className="-m-1 md:-m-1.5 flex items-center p-1 md:p-1.5">
              <span className="sr-only">Open user menu</span>
              <img
                className="h-6 w-6 md:h-7 md:w-7 lg:h-8 lg:w-8 rounded-full bg-purple-50 ring-2 ring-purple-500"
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                  username || "Admin"
                )}&background=7c3aed&color=fff&rounded=true`}
                alt={username || "Admin"}
              />
              <span className="hidden lg:flex lg:items-center">
                <span
                  className="ml-4 text-sm font-semibold leading-6 text-gray-900"
                  aria-hidden="true"
                >
                  {username || "Admin"}
                </span>
                <ChevronDownIcon
                  className="ml-2 h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                {superAdminUserNavigation.map((item) => (
                  <Menu.Item key={item.name}>
                    {({ active }) => (
                      <a
                        href={item.href}
                        className={classNames(
                          active ? "bg-gray-50" : "",
                          "block px-3 py-1 text-sm leading-6 text-gray-900 cursor-pointer"
                        )}
                        onClick={
                          item.name === "Sign out" ? handleLogout : undefined
                        }
                      >
                        {item.name}
                      </a>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  );
}

